import { Typography } from "@/components/atoms/Typography";
import { ChevronRightIcon } from "@/components/atoms/icons/ChevronRightIcon";
import { Skeleton } from "@/components/atoms/skeleton/Skeleton";
import WebAppLayout from "@/components/organisms/WebAppLayout";
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from "@/core/constants/dynatraceCatalog";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { useI18nContext, useTranslation, useUIColors, useIsAdhaJourney } from "@/core/hooks";
import { trackCatalogEvent } from "@/core/services/trackDynatraceCatalog";
import { isSmallScreen } from "@/core/utils/responsive";
import { useLocalSearchParams, useRouter } from "expo-router";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from "react-native";
import { useFaqs } from "../hooks/useFaqs";
import { searchFaqItems } from "../utils/faqSearch";
import { FAQEmptyState } from "./FAQEmptyState";
import { FAQSearchInput } from "./FAQSearchInput";
import { FaqList } from "./FaqList";

export function FAQScreenWeb() {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();
  const { t } = useTranslation();
  const router = useRouter();
  const isMobile = isSmallScreen();
  const { category, isAdhaFlow } = useLocalSearchParams<{ category?: string; isAdhaFlow?: string }>();
  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef<TextInput>(null);
  const isAdhaJourney = useIsAdhaJourney();
  const effectiveIsAdhaFlow = isAdhaFlow === 'true' || isAdhaJourney;
  const { categories, flattened, isLoading } = useFaqs(effectiveIsAdhaFlow);

  // Determine if we're showing category view or main FAQ view
  const isCategoryView = !!category;

  // White card wrappers so default Skeleton (level2) contrasts on FAQ page (also level2).
  const skeletonCardStyle = {
    width: "100%" as const,
    backgroundColor: colors.backgroundLevel1,
    borderRadius: 8,
    paddingVertical: 18,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: colors.outlineInactive,
  };

  // Get items based on view type
  const allItems = useMemo(() => {
    if (isCategoryView) {
      if (!category) return [];
      const group = categories.find((c) => c.category === category);
      return group?.items || [];
    }
    return flattened.items;
  }, [categories, category, flattened.items, isCategoryView]);

  // Filter items based on search
  const filteredItems = useMemo(() => {
    return searchFaqItems(allItems, searchText, {
      includeCategory: !isCategoryView, // Include category in search only for main view
      sortByCategory: !isCategoryView, // Sort by category only for main view
    });
  }, [allItems, searchText, isCategoryView]);

  const hasSearch = searchText.trim().length > 0;
  const hasSearchResults = filteredItems.length > 0;
  const hasData =
    (categories?.length || 0) > 0 && (flattened?.items?.length || 0) > 0;

  useCatalogDynatraceView(
    ALMETLAA_SCREEN.FAQ,
    ALMETLAA_ACTION.FAQ.VIEW,
    effectiveIsAdhaFlow && !isLoading && !isCategoryView,
  );
  const categoryTrackedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!effectiveIsAdhaFlow || !isCategoryView || !category || isLoading) {
      return;
    }
    if (categoryTrackedRef.current === category) {
      return;
    }
    categoryTrackedRef.current = category;
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.FAQ,
      actionName: ALMETLAA_ACTION.FAQ.CATEGORY_CLICK,
      eventType: "action",
      properties: { p_sectionName: category },
    });
  }, [category, effectiveIsAdhaFlow, isCategoryView, isLoading]);

  // Determine header title and breadcrumbs
  const headerTitle = isCategoryView ? category || "" : t("profile.faq");
  const homePath = "/(auth)/(home)";
  const breadcrumbs = isCategoryView
    ? [
        { label: t("common.home"), path: homePath },
        { label: t("profile.faq"), path: "/faq" },
        { label: category || "" },
      ]
    : [
        { label: t("common.home"), path: homePath },
        { label: t("profile.faq") },
      ];

  // Render content based on view type and state
  const renderContent = () => {
    // Search mode
    if (hasSearch) {
      if (isLoading) {
        return (
          <View style={styles.searchSkeletons}>
            {(['faq-search-sk-1', 'faq-search-sk-2', 'faq-search-sk-3'] as const).map(
              (id) => (
              <View
                key={id}
                style={skeletonCardStyle}
              >
                <Skeleton height={16} borderRadius={4} style={{ width: "70%" }} />
              </View>
              ),
            )}
          </View>
        );
      }
      if (hasSearchResults) {
        return <FaqList items={filteredItems} />;
      }
      return <FAQEmptyState />;
    }

    // No search - show categories (main view) or items (category view)
    if (isCategoryView) {
      // Category view: show items or loading
      if (isLoading) {
        return (
          <View style={styles.loadingPlaceholder}>
            <View style={[skeletonCardStyle, { marginBottom: 12 }]}>
              <Skeleton height={16} borderRadius={4} width="70%" />
            </View>
            <View style={[skeletonCardStyle, { marginBottom: 12 }]}>
              <Skeleton height={16} borderRadius={4} width="55%" />
            </View>
            <View style={[skeletonCardStyle, { marginBottom: 12 }]}>
              <Skeleton height={16} borderRadius={4} width="65%" />
            </View>
            <View style={skeletonCardStyle}>
              <Skeleton height={16} borderRadius={4} width="50%" />
            </View>
          </View>
        );
      }
      return <FaqList items={allItems} />;
    }

    // Main view: show categories or empty state
    if (!isLoading && !hasData) {
      return <FAQEmptyState />;
    }

    return (
      <>
        <Typography 
          variant="headline" 
          color="primary"
          style={Platform.OS === 'web' && isRTL ? { textAlign: 'right', width: '100%' } : undefined}
        >
          {t("profile.faqCategories")}
        </Typography>

        {isLoading ? (
          <View style={styles.categoriesContainer}>
            {(
              [
                'faq-category-sk-1',
                'faq-category-sk-2',
                'faq-category-sk-3',
                'faq-category-sk-4',
              ] as const
            ).map((id) => (
              <View key={id} style={skeletonCardStyle}>
                <Skeleton height={16} borderRadius={4} style={{ width: "60%" }} />
              </View>
            ))}
          </View>
        ) : (
          <View style={styles.categoriesContainer}>
            {categories.map((cat) => (
              <Pressable
                key={cat.category}
                style={[
                  styles.categoryItem,
                  {
                    backgroundColor: colors.backgroundLevel1,
                    borderColor: colors.outlineInactive,
                    flexDirection: Platform.OS === 'web' && isRTL ? 'row-reverse' : 'row',
                  },
                ]}
                onPress={() =>
                  router.push({
                    pathname: "/faq-category",
                    params: { category: cat.category, ...(effectiveIsAdhaFlow ? { isAdhaFlow: 'true' } : {}) },
                  } as any)
                }
              >
                <Typography 
                  variant="headline" 
                  color="primary" 
                  style={[
                    styles.categoryText,
                    Platform.OS === 'web' && isRTL && { textAlign: 'right' }
                  ]}
                >
                  {cat.category}
                </Typography>
                <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
                  <ChevronRightIcon size={24} color={colors.fontPrimary} />
                </View>
              </Pressable>
            ))}
          </View>
        )}

        {/* Bottom section - only show on main view */}
        <View style={styles.bottomSection}>
          <Typography
            variant="headline"
            color="primary"
            style={styles.bottomTitle}
          >
            {t("profile.faqCantFind")}
          </Typography>
          <Pressable
            style={styles.raiseRequestButton}
            onPress={() => router.push("/service-request-create" as any)}
          >
            <Typography
              variant="headline"
              color="primary"
              style={styles.raiseRequestText}
            >
              {t("profile.faqRaiseRequest")}
            </Typography>
            <View
              style={[
                styles.underline,
                { backgroundColor: colors.fontPrimary },
              ]}
            />
          </Pressable>
        </View>
      </>
    );
  };

  return (
    <WebAppLayout
      activeTab={null}
      headerTitle={headerTitle}
      showBackButton={true}
      breadcrumbs={breadcrumbs}
    >
      <View
        style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
        >
          <View style={[styles.content, isMobile && styles.contentMobile]}>
            <FAQSearchInput
              value={searchText}
              onChangeText={setSearchText}
              inputRef={searchInputRef}
            />
            {renderContent()}
          </View>
        </ScrollView>
      </View>
    </WebAppLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flexGrow: 1,
    paddingTop: 32,
    paddingHorizontal: 24,
    gap: 32,
    width: "100%",
  },
  contentMobile: {
    paddingHorizontal: 0,
  },
  scrollContent: {
    paddingBottom: 32,
    flexGrow: 1,
  },
  searchSkeletons: {
    gap: 12,
  },
  loadingPlaceholder: {
    borderRadius: 8,
  },
  categoriesContainer: {
    gap: 12,
  },
  categoryItem: {
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 16,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 3,
  },
  categoryText: {
    flex: 1,
  },
  bottomSection: {
    alignItems: "center",
    gap: 16,
    marginTop: "auto",
    paddingBottom: 24,
  },
  bottomTitle: {
    textAlign: "center",
  },
  raiseRequestButton: {
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  raiseRequestText: {
    marginBottom: 2,
  },
  underline: {
    height: 1,
    width: "100%",
  },
});
